import english from './locales/en-US/mobile-shop.json'
import { normalizeLocale } from './core'
const source: Record<string, string> = english
const TTL = 60 * 60 * 1000
const languages: Record<string, string> = { 'en': 'en-US', 'de': 'de', 'cs': 'cs', 'da': 'da', 'nl': 'nl', 'fr': 'fr', 'hu': 'hu', 'it': 'it', 'pl': 'pl', 'pt': 'pt-PT', 'ro': 'ro', 'ru': 'ru', 'es': 'es-ES', 'sv': 'sv-SE', 'tr': 'tr' }
const placeholders = (text: string) => [...text.matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map(match => match[1]).sort().join(',')
export function validateCrowdinMessages(value: unknown): Record<string, string> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
    return Object.fromEntries(Object.entries(value).filter(([key, text]) => Object.prototype.hasOwnProperty.call(source, key) && typeof text === 'string' && text.trim() && placeholders(text) === placeholders(source[key])))
}
function distributionBase(manifest: string): URL | undefined {
    try {
        const url = new URL(manifest)
        if (url.protocol !== 'https:' || url.hostname !== 'distributions.crowdin.net' || !/^\/[^/]+\/manifest\.json$/.test(url.pathname) || url.search || url.hash || url.username || url.password) return
        return new URL('./', url)
    } catch { return }
}
export async function loadCrowdinMessages(manifest: string, locale: string, signal?: AbortSignal): Promise<Record<string, string>> {
    const base = distributionBase(manifest)
    if (!base || signal?.aborted) return {}
    const normalized = normalizeLocale(locale)
    const language = normalized === 'pt-BR' ? 'pt-BR' : languages[normalized.split('-')[0]] || normalized
    const cacheKey = `mobile-shop:crowdin:v1:${base.href}:${language}`
    let cached: Record<string, string> = {}
    try {
        const entry = JSON.parse(localStorage.getItem(cacheKey) || 'null')
        cached = validateCrowdinMessages(entry?.messages)
        if ((Object.keys(cached).length || entry?.empty === true) && typeof entry?.time === 'number' && Date.now() - entry.time >= 0 && Date.now() - entry.time < TTL) return cached
    } catch { /* Storage can be disabled or full. */ }
    const controller = new AbortController()
    const abort = () => controller.abort()
    signal?.addEventListener('abort', abort, { once: true })
    const timer = setTimeout(abort, 4000)
    try {
        const options: RequestInit = { credentials: 'omit', signal: controller.signal }
        const response = await fetch(manifest, options)
        if (!response.ok) return cached
        const data = await response.json()
        const paths: unknown = data?.content?.[language] ?? data?.content?.[normalized]
        if (!Array.isArray(paths)) return cached
        const path = paths.find((path: unknown) => typeof path === 'string' && /^\/?content\/[^/]+\/mobile-shop\.json$/.test(path))
        if (typeof path !== 'string') return cached
        const url = new URL(path.replace(/^\//, ''), base)
        if (!url.href.startsWith(base.href)) return cached
        const translated = await fetch(url.href, options)
        if (!translated.ok) return cached
        const raw: unknown = await translated.json()
        const messages = validateCrowdinMessages(raw)
        const empty = !!raw && typeof raw === 'object' && Object.keys(raw).length === 0
        if (controller.signal.aborted || (!empty && !Object.keys(messages).length)) return cached
        try { localStorage.setItem(cacheKey, JSON.stringify({ time: Date.now(), messages, empty })) } catch { /* Optional cache. */ }
        return messages
    } catch { return cached } finally {
        clearTimeout(timer)
        signal?.removeEventListener('abort', abort)
    }
}
